<template>
    <div class="container" v-if="!isDataRequired">
        <v-title :lineage="[{'Users':'users'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} User
            <div slot="actions">
                <v-cancel :defaultRoute="{name: 'users'}"></v-cancel>
                <r-button type="primary" @click="save(user)" :loading="isLoading">Save User</r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="isFormUpdating && newRecord"
            type="positive"
            title="User successfully created"
            class="stack-l">
            <span slot="content">
                Would you like to add <r-button type="link" @click="createNew">another user</r-button> or go back to the <router-link :to="{name: 'users'}">user list</router-link>?
            </span>
        </v-inline-notifications>
        <v-error-messages
            :has-frontend-errors="$v.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.user.firstName.required">User first name is required</li>
            <li class="list-item" v-if="!$v.user.lastName.required">User last name is required</li>
            <li class="list-item" v-if="!$v.user.email.required">User email is required</li>
            <li class="list-item" v-if="!$v.user.email.email">User email is invalid</li>
            <li class="list-item" v-if="!$v.user.permissions.required">Limited access users require at least one access restriction</li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">User Info</h2>
            <p slot="description" class="text-muted">This person will receive an email invitation upon completion of this form.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input auto-focus label="First Name" v-model="user.firstName"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input label="Last Name" v-model="user.lastName"/>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input label="Email" v-model="user.email" :disabled="isFormUpdating"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <permission-form v-model="user.permissions" :user-id="user.id"/>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :defaultRoute="{name: 'users'}"></v-cancel>
                <r-button type="primary" @click="save(user)" :loading="isLoading">Save User</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vCancel, vErrorMessages, vFormTile,
    } from '@/components/form';
    import UserModel from '@/common/models/user-model';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import _ from '@/common/helpers';
    import {required, email, requiredIf} from 'vuelidate/lib/validators';
    import PermissionForm from './components/permission-form';

    export default {
        name: 'user-form',
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vInlineNotifications,
            vFormTile,
            vCancel,
            PermissionForm,
            vErrorMessages,
        },
        data() {
            return {
                user: new UserModel(),
            };
        },
        validations: {
            user: {
                firstName: {required},
                lastName: {required},
                email: {required, email},
                permissions: {
                    required: requiredIf(
                        ({permissions}) => permissions && _.isEmpty(permissions),
                    ),
                },
            },
        },
        methods: {
            createNew() {
                this.user = new UserModel();
                this.$router.push({name: 'add-user'});
                this.$v.$reset();
            },
            async update(user) {
                const params = {
                    id: user.id,
                    data: user.getPayload(),
                };
                try {
                    const {fields} = await this.$api(({users}) => users.update(params));
                    this.user = new UserModel(fields);
                    this.$toast.positive('User successfully updated');
                } catch (error) {
                    this.$toast.fromError(error);
                    throw error;
                }
            },
            async create(user) {
                const data = user.getPayload();
                try {
                    const {fields} = await this.$api(({users}) => users.create({data}));
                    this.user = new UserModel(fields);
                    this.isDataReady = true;
                    this.$router.push({name: 'update-user', params: {id: fields.id}, query: {newRecord: true}});
                    this.$toast.positive('User successfully created');
                } catch (error) {
                    this.$toast.fromError(error);
                    throw error;
                }
            },
            async fetchData() {
                const params = {id: this.id};
                try {
                    const {fields} = await this.$api(({users}) => users.get(params));
                    this.user = new UserModel(fields);
                } catch (error) {
                    this.$toast.fromError(error);
                    this.$redirectOnError(error, {name: 'users'});
                }
            },
        },
    };
</script>
